import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL = 3000;

export const useScrapeBatchStatus = (clientId, options = {}) => {
  const [batch, setBatch] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const onCompleteRef = useRef(options.onComplete);
  const previouslyActiveRef = useRef(false);
  const completedBatchIdsRef = useRef(new Set());

  const storageKey = `scrape_batch_${clientId}`;

  useEffect(() => {
    onCompleteRef.current = options.onComplete;
  }, [options.onComplete]);

  useEffect(() => {
    if (clientId) {
      const storedBatchId = localStorage.getItem(storageKey);
      if (storedBatchId) {
        setBatchId(storedBatchId);
      }
    }
  }, [clientId, storageKey]);

  const fetchStatus = useCallback(async () => {
    if (!clientId || !batchId) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/knowledge/${clientId}/scrape-status`,
        {
          params: { batch_id: batchId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const newBatch = response.data.batch;
      setBatch(newBatch);
      setTasks(response.data.tasks || []);
      setSummary(response.data.summary);
      setError(null);

      if (previouslyActiveRef.current && !newBatch.is_active && !completedBatchIdsRef.current.has(batchId)) {
        completedBatchIdsRef.current.add(batchId);
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }

      previouslyActiveRef.current = newBatch.is_active;
    } catch (err) {
      console.error('Failed to fetch scrape status:', err);
      setError(err.response?.data?.detail || 'Failed to fetch scrape status');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, batchId]);

  useEffect(() => {
    if (!batchId) return;

    fetchStatus();

    if (batch?.is_active === false) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchStatus();
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [batchId, fetchStatus, batch?.is_active]);

  const clearBatch = useCallback(() => {
    localStorage.removeItem(storageKey);
    setBatchId(null);
    setBatch(null);
    setTasks([]);
    setSummary(null);
    setError(null);
    previouslyActiveRef.current = false;
  }, [storageKey]);

  const startTracking = useCallback((newBatchId) => {
    localStorage.setItem(storageKey, newBatchId);
    setBatchId(newBatchId);
    previouslyActiveRef.current = false;
  }, [storageKey]);

  const cancelBatch = useCallback(async () => {
    if (!clientId || !batchId) return;

    try {
      setIsCancelling(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/knowledge/${clientId}/scrape-cancel`,
        null,
        {
          params: { batch_id: batchId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(response.data.message || 'Scraping cancelled successfully');
      
      await fetchStatus();
    } catch (err) {
      console.error('Failed to cancel scraping:', err);
      toast.error(err.response?.data?.detail || 'Failed to cancel scraping');
    } finally {
      setIsCancelling(false);
    }
  }, [clientId, batchId, fetchStatus]);

  return {
    batch,
    tasks,
    summary,
    isLoading,
    error,
    clearBatch,
    startTracking,
    hasBatch: !!batchId,
    cancelBatch,
    isCancelling
  };
};
